import { useEffect, useState } from 'react';
import { useCompany } from '../../context/CompanyContext';
import ImportModal from '../../components/ImportModal';
import api from '../../api';

function sectionLabel(s) {
  const numbers =
    !s.old_section || s.old_section === s.new_section
      ? s.new_section
      : `${s.old_section} / ${s.new_section}`;
  return `${numbers} — ${s.description || ''}`.trim();
}

function emptyRow(sections) {
  const first = sections[0];
  if (!first) {
    return { section: '', nature: '', gross_amount: '', rate: null, tds_amount: '' };
  }
  return {
    section: first.new_section, nature: first.description, gross_amount: '', rate: first.rate_percent,
    tds_amount: '',
  };
}

function inr(amount) {
  return `₹${Number(amount || 0).toLocaleString('en-IN')}`;
}

function TdsEntryTab() {
  const { selectedCompanyId } = useCompany();
  const today = new Date();
  const [sections, setSections] = useState([]);
  const [quarter, setQuarter] = useState('Q1');
  const [year, setYear] = useState(today.getFullYear());
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    api.get('/tds-sections', { params: { active_only: true } }).then((res) => {
      setSections(res.data);
      setRows((prev) => (prev.length === 0 ? [emptyRow(res.data)] : prev));
    });
  }, []);

  const loadData = () => {
    if (!selectedCompanyId) return;
    api
      .get(`/tds-data/${selectedCompanyId}`, { params: { quarter, year } })
      .then((res) => {
        setSaved(false);
        if (res.data.length === 0) {
          setRows([emptyRow(sections)]);
        } else {
          setRows(
            res.data.map((r) => ({
              section: r.section, nature: r.nature, gross_amount: r.gross_amount,
              rate: r.rate, tds_amount: r.tds_amount,
            }))
          );
        }
      })
      .catch((err) => setError(err.message));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadData, [selectedCompanyId, quarter, year]);

  const updateRow = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      const row = { ...next[index], [field]: value };

      if (field === 'section') {
        const info = sections.find((s) => s.new_section === value);
        if (info) {
          row.nature = info.description;
          row.rate = info.rate_percent;
        }
      }

      if (field === 'section' || field === 'gross_amount' || field === 'rate') {
        const gross = Number(field === 'gross_amount' ? value : row.gross_amount) || 0;
        const rate = Number(field === 'rate' ? value : row.rate) || 0;
        if (row.rate !== null && row.section !== '192') {
          row.tds_amount = Math.round((gross * rate) / 100);
        }
      }

      next[index] = row;
      return next;
    });
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow(sections)]);
  const removeRow = (index) => setRows((prev) => prev.filter((_, i) => i !== index));

  const totalTds = rows.reduce((sum, r) => sum + (Number(r.tds_amount) || 0), 0);

  const handleSave = (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload = {
      company_id: selectedCompanyId,
      quarter,
      year,
      rows: rows.map((r) => ({
        section: r.section,
        nature: r.nature,
        gross_amount: Number(r.gross_amount) || 0,
        rate: Number(r.rate) || 0,
        tds_amount: Number(r.tds_amount) || 0,
      })),
    };
    api
      .post('/tds-data', payload)
      .then(() => setSaved(true))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setSaving(false));
  };

  return (
    <form className="dash-card" onSubmit={handleSave}>
      <div className="page-title-row" style={{ marginBottom: 0 }}>
        <h2 className="dash-card-title">TDS Data Entry</h2>
        <button className="dash-btn cal-btn-grey" type="button" onClick={() => setImportOpen(true)}>
          Import
        </button>
      </div>

      <div className="de-period-row">
        <div className="field-group">
          <span className="field-label">Quarter</span>
          <select className="field-select" value={quarter} onChange={(e) => setQuarter(e.target.value)}>
            <option value="Q1">Q1 (Apr-Jun)</option>
            <option value="Q2">Q2 (Jul-Sep)</option>
            <option value="Q3">Q3 (Oct-Dec)</option>
            <option value="Q4">Q4 (Jan-Mar)</option>
          </select>
        </div>
        <div className="field-group">
          <span className="field-label">Year</span>
          <input className="field-input" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
        </div>
      </div>

      {error && <p className="dash-error">{error}</p>}

      <table className="de-rows-table">
        <thead>
          <tr>
            <th>Section</th>
            <th>Nature</th>
            <th>Gross Amount ₹</th>
            <th>Rate %</th>
            <th>TDS Amount ₹</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <td>
                <select value={row.section} onChange={(e) => updateRow(i, 'section', e.target.value)}>
                  {/* A saved row may reference a section that has since been
                      deactivated (e.g. an old-act code after the 2025 Act
                      migration). Without this the select would silently fall
                      back to the first option and rewrite the section on save. */}
                  {row.section && !sections.some((s) => s.new_section === row.section) && (
                    <option value={row.section}>{row.section} — (no longer listed)</option>
                  )}
                  {sections.map((s) => (
                    <option key={s.id} value={s.new_section}>{sectionLabel(s)}</option>
                  ))}
                </select>
              </td>
              <td>{row.nature}</td>
              <td>
                <input
                  type="number"
                  value={row.gross_amount}
                  onChange={(e) => updateRow(i, 'gross_amount', e.target.value)}
                />
              </td>
              <td>{row.rate === null ? 'Slab' : `${row.rate}%`}</td>
              <td>
                <input
                  type="number"
                  value={row.tds_amount}
                  onChange={(e) => updateRow(i, 'tds_amount', e.target.value)}
                  readOnly={row.rate !== null}
                />
              </td>
              <td>
                <button className="de-remove-row" type="button" onClick={() => removeRow(i)} title="Remove row">
                  ✕
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="dash-btn-row">
        <button className="dash-btn dash-btn-outline" type="button" onClick={addRow}>+ Add Row</button>
        <button className="dash-btn dash-btn-orange" type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save TDS Data'}
        </button>
      </div>

      {saved && <div className="de-summary">Saved — Total TDS for {quarter} {year}: {inr(totalTds)}</div>}

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        module="tds_data"
        moduleLabel="TDS Data Entry"
        companyId={selectedCompanyId}
        onImported={loadData}
        supportsCreateNew
      />
    </form>
  );
}

export default TdsEntryTab;
